import type { ToastVariant } from 'pi-kiosk-shared/ui';

/** Soft cap so a runaway caller cannot grow the queue without bound. */
export const MAX_QUEUED_TOASTS = 20;

export const DEFAULT_TOAST_DURATION_MS = 4000;

export interface ShowToastOptions {
  readonly message: string;
  readonly variant?: ToastVariant;
  readonly durationMs?: number;
}

export interface ToastRecord {
  readonly id: string;
  readonly message: string;
  readonly variant: ToastVariant;
  readonly durationMs: number;
}

type ToastListener = () => void;

let sequence = 0;
let toasts: readonly ToastRecord[] = [];
const listeners = new Set<ToastListener>();

function createToastId(): string {
  sequence += 1;
  return `customer-toast-${sequence}`;
}

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

function setToasts(next: readonly ToastRecord[]): void {
  toasts = next;
  emit();
}

/** Subscribe to queue changes (useSyncExternalStore). */
export function subscribeToasts(listener: ToastListener): () => void {
  listeners.add(listener);
  return (): void => {
    listeners.delete(listener);
  };
}

export function getToastSnapshot(): readonly ToastRecord[] {
  return toasts;
}

/**
 * Enqueue a toast. Returns the assigned id (for programmatic dismiss).
 * Host enforces maxVisible; this store holds the full FIFO queue.
 * Identical message+variant already queued is not duplicated (return existing id).
 */
export function showToast(options: ShowToastOptions): string {
  const variant = options.variant ?? 'default';
  const existing = toasts.find(
    (toast) => toast.message === options.message && toast.variant === variant,
  );
  if (existing !== undefined) {
    return existing.id;
  }
  const id = createToastId();
  const record: ToastRecord = {
    id,
    message: options.message,
    variant,
    durationMs: options.durationMs ?? DEFAULT_TOAST_DURATION_MS,
  };
  const merged = [...toasts, record];
  setToasts(
    merged.length <= MAX_QUEUED_TOASTS
      ? merged
      : merged.slice(merged.length - MAX_QUEUED_TOASTS),
  );
  return id;
}

export function dismissToast(id: string): void {
  const next = toasts.filter((toast) => toast.id !== id);
  if (next.length === toasts.length) {
    return;
  }
  setToasts(next);
}

/** Test / HMR helper — clears the module queue. */
export function resetToastStore(): void {
  toasts = [];
  emit();
}

/** Live-region politeness per LOCKED #9 / Wave A.3. */
export function resolveToastAriaLive(
  variant: ToastVariant,
): 'polite' | 'assertive' {
  return variant === 'danger' || variant === 'warn' ? 'assertive' : 'polite';
}
